import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, AlertTriangle, Lightbulb, Users, Loader2, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Step = ({ ok, warn, title, children, testId }) => (
  <div className="flex gap-3" data-testid={testId}>
    <div className="pt-0.5 shrink-0">
      {warn ? <AlertTriangle className="w-5 h-5 text-[#FF9F0A]" /> : ok ? <CheckCircle2 className="w-5 h-5 text-[#34C759]" /> : <Loader2 className="w-5 h-5 animate-spin text-[var(--text-muted)]" />}
    </div>
    <div className="min-w-0">
      <p className="text-sm font-semibold text-[var(--text-primary)]">{title}</p>
      <div className="text-sm text-[var(--text-secondary)] leading-relaxed">{children}</div>
    </div>
  </div>
);

const COACHING = "Let's get through this sticking point by exposing the logic. You've come this far, so make the most of this moment. A good puzzle is made of littler puzzles, and once you unlock the littler puzzle, the other answers fall into place in a cascade. We don't want you to miss what you came for.";

const CommunityForm = ({ user, busy, onSubmit }) => {
  const [note, setNote] = useState('');
  const [emailMe, setEmailMe] = useState(false);
  return (
    <div className="space-y-3">
      <Textarea
        value={note}
        onChange={(e) => setNote(e.target.value.slice(0, 500))}
        placeholder="Optional: where are you stuck? (e.g. box 5, or I can't find the next single)"
        className="min-h-[70px] text-sm bg-[var(--background-main)] border-[var(--border-subtle)]"
        data-testid="stuck-note-input"
      />
      {user?.email && (
        <label className="flex items-start gap-2 text-xs text-[var(--text-secondary)] cursor-pointer">
          <Checkbox checked={emailMe} onCheckedChange={(v) => setEmailMe(!!v)} data-testid="stuck-email-checkbox" />
          <span>Also send replies to my email ({user.email}), in addition to showing them next to my grid. Your email is never posted.</span>
        </label>
      )}
      <Button
        onClick={() => onSubmit(note, emailMe)}
        disabled={busy}
        className="w-full rounded-full bg-[#8B5A3C] hover:bg-[#A46944] text-[#FFF3E4]"
        data-testid="stuck-ask-community-btn"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Users className="w-4 h-4 mr-2" />}
        Ask the community
      </Button>
    </div>
  );
};

export const StuckDialog = ({ open, onOpenChange, puzzleId, grid, pencilMarks, user, helpRequest, onShowWrong, onHint, onRequestCreated, onOpenThread }) => {
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !puzzleId) return;
    setCheck(null);
    axios.post(`${API_URL}/api/puzzles/${puzzleId}/stuck-check`, { grid }, { withCredentials: true })
      .then((r) => setCheck(r.data))
      .catch((e) => { toast.error(e.response?.data?.detail || 'Could not check the puzzle.'); onOpenChange(false); });
  }, [open, puzzleId]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (note, emailMe) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/help-requests`, {
        puzzle_id: puzzleId, grid, pencil_marks: pencilMarks, note: note || null, email_replies: emailMe,
      }, { withCredentials: true });
      onRequestCreated(data);
      toast.success(data.posted ? 'Sent to the SudokuAcademy.ORG community.' : 'Help request saved. It goes out to the community shortly.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send your help request.');
    } finally {
      setBusy(false);
    }
  };

  const clean = check && check.wrong_count === 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90dvh] overflow-y-auto bg-[var(--background-surface)] border-[var(--border-subtle)]" data-testid="stuck-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-[var(--text-primary)]">Stuck? Let's work through it.</DialogTitle>
          <DialogDescription className="text-[var(--text-muted)]">These puzzles can be challenging, so we check everything first.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 pt-1">
          <Step ok={!!check} warn={check && !check.unique_solution} title="Validator" testId="stuck-step-validator">
            {!check ? 'Checking the puzzle…' : check.unique_solution
              ? `This puzzle has exactly one solution${check.logic_verified ? ' and can be solved by pure logic, no guessing' : ''}.`
              : 'We could not confirm a unique solution. Please report it below and we will take a look.'}
          </Step>
          <Step ok={!!check} warn={check && check.wrong_count > 0} title="Check my work" testId="stuck-step-checkwork">
            {!check ? 'Comparing your entries…' : clean ? 'No mistakes so far. Every entry you placed is correct.' : (
              <>
                {check.wrong_count} {check.wrong_count === 1 ? 'entry doesn\'t' : 'entries don\'t'} match the solution. Fixing {check.wrong_count === 1 ? 'it' : 'them'} may unblock you.{' '}
                <button className="underline text-[#8B5A3C] font-medium" onClick={() => { onShowWrong(check.wrong_cells); onOpenChange(false); }} data-testid="stuck-highlight-wrong-btn">Highlight them</button>
              </>
            )}
          </Step>
          {check && (
            <Step ok title="Try the solver for the next move" testId="stuck-step-solver">
              <p className="mb-2" data-testid="stuck-coaching-text">{COACHING}</p>
              <Button variant="outline" size="sm" onClick={() => { onOpenChange(false); onHint(); }} className="rounded-full border-[#8B5A3C]/50 text-[#8B5A3C]" data-testid="stuck-show-next-step-btn">
                <Lightbulb className="w-4 h-4 mr-1.5" /> Show me the next step
              </Button>
            </Step>
          )}
          {check && (
            <Step ok title="Ask the SudokuAcademy.ORG community" testId="stuck-step-community">
              {helpRequest && helpRequest.status === 'open' ? (
                <div className="space-y-2">
                  <p>Your request is with the community{helpRequest.community_reply_count ? ` and has ${helpRequest.community_reply_count} ${helpRequest.community_reply_count === 1 ? 'reply' : 'replies'}` : ''}.</p>
                  <Button size="sm" onClick={() => { onOpenChange(false); onOpenThread(); }} className="rounded-full bg-[#8B5A3C] hover:bg-[#A46944] text-[#FFF3E4]" data-testid="stuck-view-replies-btn">
                    <MessageCircle className="w-4 h-4 mr-1.5" /> View replies
                  </Button>
                </div>
              ) : helpRequest && helpRequest.status === 'escalated' ? (
                <p data-testid="stuck-escalated-text">You've reported this puzzle to our team, with your grid and the community thread attached. Thank you for sticking with it.</p>
              ) : (
                <>
                  <p className="mb-3">We'll share a snapshot of your grid with our Discord community, noting that the puzzle is validated and your progress is checked. Replies show up right here, next to your puzzle. Only your first name is shown.</p>
                  <CommunityForm user={user} busy={busy} onSubmit={submit} />
                </>
              )}
            </Step>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
